import { useRef, useState } from 'react';
import type { StepProps, AnalysisResult } from '../OBWWizard';

// Phase 5c.5 (ADR-030 D2 Step 8) — logo dropzone (drag-and-drop + click-to-
// upload with preview) + brand colour pickers. The captured logos are committed
// to the step payload and persisted server-side to S3 + `brandAssets` by the
// OBW brand-step handler (ADR-087 write-path A) — the modal never touches S3.

export function StepBrand({ data, analysisResult, onNext, isRequired, presentation }: StepProps & { analysisResult?: AnalysisResult | null }) {
  const ai = analysisResult;
  // ADR-073 Am.5 §A5.6 — condensed for the public modal's frame (no scrolling): each logo is ONE row
  // (preview + upload button), the colours share a row, and the closing hint is dropped. Same
  // answers, same upload path.
  const compact = presentation === 'embedded';

  const [brandTone, setBrandTone] = useState<string>(
    (data?.brandTone as string) ?? ai?.brandTone ?? '',
  );
  const [primaryColour, setPrimaryColour] = useState<string>(
    (data?.primaryColour as string) ?? '#3b82f6',
  );
  const [secondaryColour, setSecondaryColour] = useState<string>(
    (data?.secondaryColour as string) ?? '#22c55e',
  );
  const [logoDarkPreview, setLogoDarkPreview] = useState<string | null>(
    (data?.logoDarkPreview as string) ?? null,
  );
  const [logoLightPreview, setLogoLightPreview] = useState<string | null>(
    (data?.logoLightPreview as string) ?? null,
  );
  const [dragOver, setDragOver] = useState<'dark' | 'light' | null>(null);

  const darkInputRef = useRef<HTMLInputElement>(null);
  const lightInputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File, variant: 'dark' | 'light') {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      if (variant === 'dark') setLogoDarkPreview(result);
      else setLogoLightPreview(result);
    };
    reader.readAsDataURL(file);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>, variant: 'dark' | 'light') {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file, variant);
  }

  function commit() {
    onNext({
      brandTone,
      primaryColour,
      secondaryColour,
      logoDarkPreview,
      logoLightPreview,
    });
  }

  if (compact) {
    const logoRow = (variant: 'dark' | 'light', preview: string | null, ref: React.RefObject<HTMLInputElement>) => (
      <div className="obw-logo-row">
        <div className={`obw-logo-preview obw-logo-preview-compact ${variant}`} aria-label={`${variant === 'dark' ? 'Dark' : 'Light'} variant preview`}>
          {preview ? <img src={preview} alt={`${variant === 'dark' ? 'Dark' : 'Light'} logo preview`} /> : <span>No logo</span>}
        </div>
        <button type="button" className="obw-btn-secondary obw-logo-row-button" onClick={() => ref.current?.click()}>
          {variant === 'dark' ? 'Logo for dark backgrounds' : 'Logo for light backgrounds'}
        </button>
        <input
          ref={ref}
          type="file"
          accept="image/*"
          hidden
          aria-label={`Upload ${variant} variant logo`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f, variant); }}
        />
      </div>
    );
    return (
      <div className="obw-step-content obw-brand-compact">
        <h2 className="obw-title">Brand</h2>
        <p className="obw-subtitle">How your reports and dashboard look. Change it any time in Manage Account.</p>
        <p className="obw-form-section">Logo (SVG or PNG)</p>
        {logoRow('dark', logoDarkPreview, darkInputRef)}
        {logoRow('light', logoLightPreview, lightInputRef)}
        <div className="obw-form-grid-2">
          <div className="obw-color-picker">
            <label htmlFor="obw-primary-colour" className="obw-form-label">Primary colour</label>
            <input id="obw-primary-colour" type="color" value={primaryColour} onChange={(e) => setPrimaryColour(e.target.value)} />
          </div>
          <div className="obw-color-picker">
            <label htmlFor="obw-secondary-colour" className="obw-form-label">Secondary colour</label>
            <input id="obw-secondary-colour" type="color" value={secondaryColour} onChange={(e) => setSecondaryColour(e.target.value)} />
          </div>
        </div>
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="obw-brand-tone">How your brand sounds</label>
          <input
            id="obw-brand-tone"
            className="obw-form-input"
            type="text"
            value={brandTone}
            onChange={(e) => setBrandTone(e.target.value)}
            placeholder="e.g. professional, friendly"
          />
        </div>
        <div className="portal-wizard-nav">
          <button type="button" className="obw-btn-primary" onClick={commit}>Continue</button>
        </div>
      </div>
    );
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Brand</h2>
      <p className="obw-subtitle">
        Your brand identity drives how trust signals are presented in customer-facing reports and
        your in-portal dashboard.
        {ai && " We've pre-filled brand tone from your website — adjust as needed."}
      </p>

      {/* Logo dropzones — dark + light variants */}
      <p className="obw-form-section">Logo</p>
      <div className="obw-logo-zone">
        <div className="obw-logo-zone-title">
          <span>Dark-background variant</span>
          <span className="obw-logo-zone-spec">SVG / PNG · transparent · ~400×120</span>
        </div>
        <div className="obw-logo-zone-body">
          <div className="obw-logo-preview dark" aria-label="Dark variant preview">
            {logoDarkPreview ? (
              <img src={logoDarkPreview} alt="Dark logo preview" />
            ) : (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>No logo yet</span>
            )}
          </div>
          <div
            className={`obw-logo-dropzone${dragOver === 'dark' ? ' dragover' : ''}`}
            onDrop={(e) => onDrop(e, 'dark')}
            onDragOver={(e) => { e.preventDefault(); setDragOver('dark'); }}
            onDragLeave={() => setDragOver(null)}
            onClick={() => darkInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') darkInputRef.current?.click(); }}
            aria-label="Upload dark variant logo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 24, height: 24 }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span className="obw-logo-dropzone-label">Drop or click to upload</span>
            <span className="obw-logo-dropzone-hint">Used on dark backgrounds</span>
            <input
              ref={darkInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f, 'dark'); }}
            />
          </div>
        </div>
      </div>

      <div className="obw-logo-zone">
        <div className="obw-logo-zone-title">
          <span>Light-background variant</span>
          <span className="obw-logo-zone-spec">SVG / PNG · transparent · ~400×120</span>
        </div>
        <div className="obw-logo-zone-body">
          <div className="obw-logo-preview light" aria-label="Light variant preview">
            {logoLightPreview ? (
              <img src={logoLightPreview} alt="Light logo preview" />
            ) : (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>No logo yet</span>
            )}
          </div>
          <div
            className={`obw-logo-dropzone${dragOver === 'light' ? ' dragover' : ''}`}
            onDrop={(e) => onDrop(e, 'light')}
            onDragOver={(e) => { e.preventDefault(); setDragOver('light'); }}
            onDragLeave={() => setDragOver(null)}
            onClick={() => lightInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') lightInputRef.current?.click(); }}
            aria-label="Upload light variant logo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 24, height: 24 }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span className="obw-logo-dropzone-label">Drop or click to upload</span>
            <span className="obw-logo-dropzone-hint">Used on light backgrounds</span>
            <input
              ref={lightInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f, 'light'); }}
            />
          </div>
        </div>
      </div>

      {/* Brand colours */}
      <p className="obw-form-section">Brand colours</p>
      <div className="obw-color-pickers">
        <div className="obw-color-picker">
          <label htmlFor="obw-primary-colour" className="obw-form-label">Primary</label>
          <input
            id="obw-primary-colour"
            type="color"
            value={primaryColour}
            onChange={(e) => setPrimaryColour(e.target.value)}
          />
          <span style={{ fontFamily: 'monospace', color: 'var(--text-dim)', fontSize: '12px' }}>{primaryColour}</span>
        </div>
        <div className="obw-color-picker">
          <label htmlFor="obw-secondary-colour" className="obw-form-label">Secondary</label>
          <input
            id="obw-secondary-colour"
            type="color"
            value={secondaryColour}
            onChange={(e) => setSecondaryColour(e.target.value)}
          />
          <span style={{ fontFamily: 'monospace', color: 'var(--text-dim)', fontSize: '12px' }}>{secondaryColour}</span>
        </div>
      </div>

      {/* Brand tone */}
      <p className="obw-form-section">Brand tone</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="obw-brand-tone">How your brand sounds</label>
        <input
          id="obw-brand-tone"
          className="obw-form-input"
          type="text"
          value={brandTone}
          onChange={(e) => setBrandTone(e.target.value)}
          placeholder="e.g. professional, friendly, authoritative"
        />
      </div>

      <p className="obw-form-hint">Your logos are saved to your brand profile and appear in the portal navbar and on your domain cards. You can change them any time from Manage Account → Brand Presence.</p>

      <div className="portal-wizard-nav">
        {!isRequired && (
          <button type="button" className="btn-secondary" onClick={commit}>Skip for now</button>
        )}
        <button type="button" className="obw-btn-primary" onClick={commit}>Continue</button>
      </div>
    </div>
  );
}
